import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { PortableText } from '@portabletext/react';
import { client, urlFor } from '../../sanity/client';
import SEO from '../SEO/SEO';
import { ArticleSchema, BreadcrumbSchema } from '../SEO/StructuredData';
import PageHero from '../common/PageHero';

interface SolutionDetail {
  _id?: string;
  slug?: string;
  language?: string;
  translationGroupId?: string;
  title: string;
  mainImage: any;
  body: any;
  publishedAt: string;
  author: string;
  excerpt: string;
  headingPairs?: { label: string; anchor: string }[];
}

const HERO_IMAGE_WIDTHS = [640, 960, 1280, 1600, 1800];
const BODY_IMAGE_WIDTHS = [480, 768, 1024, 1280];

const buildImageSrcSet = (image: any, widths: number[], aspectRatio?: number) =>
  widths
    .map((width) => {
      const builder = urlFor(image).width(width).auto('format').quality(70);
      if (aspectRatio) {
        const height = Math.round(width / aspectRatio);
        return `${builder.height(height).fit('crop').url()} ${width}w`;
      }
      return `${builder.fit('max').url()} ${width}w`;
    })
    .join(', ');

type SolutionLookupDoc = {
  _id?: string;
  slug?: string;
  translationGroupId?: string;
};

const slugify = (value: string) =>
  encodeURIComponent(
    value
      .toLowerCase()
      .replace(/[^a-z0-9\u0080-\uFFFF]+/g, '-')
      .replace(/(^-|-$)/g, '')
  );

const normalizeSearchText = (value: string) =>
  value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\([^)]*\)/g, '')
    .replace(/\bmaintance\b/g, 'maintenance')
    .replace(/[^a-z0-9\u0080-\uFFFF\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getSearchTokens = (value: string): string[] =>
  normalizeSearchText(value)
    .split(' ')
    .map((token) => token.trim())
    .filter((token) => token.length > 0);

const isTokenMatch = (left: string, right: string): boolean => {
  const leftTokens = getSearchTokens(left);
  const rightTokens = new Set(getSearchTokens(right));
  if (leftTokens.length === 0 || rightTokens.size === 0) return false;
  const matches = leftTokens.filter((token) => rightTokens.has(token)).length;
  return matches >= 2 || matches / leftTokens.length >= 0.6;
};

const normalizeAnchorText = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getBlockText = (block: any): string =>
  (block?.children || [])
    .map((child: any) => child?.text || '')
    .join('')
    .trim();

const normalizeHeadingLabel = (label: string): string => label.replace(/^\d+[.)]\s*/, '').trim();
const toI18nKey = (value: string): string =>
  value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const isNumberedSectionLabel = (label: string): boolean => /^\d+[.)]\s+\S+/.test(label);

const isTocBlock = (block: any): boolean => {
  if (block?._type !== 'block') return false;
  if (block?.style === 'h2' || block?.style === 'h3') return true;
  if (block?.style !== 'normal') return false;
  const text = getBlockText(block);
  return isNumberedSectionLabel(text);
};

const extractHeadingsFromBody = (body: any): { label: string; anchor: string }[] => {
  if (!Array.isArray(body)) return [];

  return body
    .filter((block: any) => isTocBlock(block))
    .map((block: any) => {
      const label = normalizeHeadingLabel(getBlockText(block));
      return label ? { label, anchor: slugify(label) } : null;
    })
    .filter(Boolean) as { label: string; anchor: string }[];
};

const SolutionPostSkeleton: React.FC = () => (
  <article className="bg-slate-50 pb-16 sm:pb-20 animate-pulse">
    <section className="bg-[#0A1628] py-24 relative overflow-hidden">
      <div className="relative max-w-6xl mx-auto px-6">
        <div className="mb-8 h-10 w-40 rounded-lg bg-white/10" />
        <div className="text-center">
          <div className="mx-auto mb-4 h-3 w-28 rounded bg-teal-400/30" />
          <div className="mx-auto h-10 w-full max-w-4xl rounded bg-white/10" />
          <div className="mx-auto mt-4 h-10 w-3/4 max-w-3xl rounded bg-white/10" />
          <div className="mx-auto mt-6 h-5 w-2/3 max-w-2xl rounded bg-white/10" />
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <div className="h-8 w-36 rounded-full bg-white/10" />
            <div className="h-8 w-40 rounded-full bg-white/10" />
            <div className="h-8 w-28 rounded-full bg-white/10" />
          </div>
        </div>
      </div>
    </section>

    <div className="mx-auto max-w-[1240px] px-4 pt-6 sm:px-6 sm:pt-8 lg:px-8 lg:pt-10">
      <div className="h-[320px] w-full rounded-2xl border border-slate-200 bg-slate-200/70 shadow-sm sm:h-[420px]" />
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-12">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8 lg:p-10">
          <div className="h-8 w-3/4 rounded bg-slate-200" />
          <div className="mt-6 h-4 w-full rounded bg-slate-200" />
          <div className="mt-3 h-4 w-full rounded bg-slate-200" />
          <div className="mt-3 h-4 w-5/6 rounded bg-slate-200" />
          <div className="mt-8 h-7 w-2/3 rounded bg-slate-200" />
          <div className="mt-4 h-4 w-full rounded bg-slate-200" />
          <div className="mt-3 h-4 w-11/12 rounded bg-slate-200" />
          <div className="mt-3 h-4 w-4/5 rounded bg-slate-200" />
        </div>
        <aside className="hidden lg:block">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="h-3 w-28 rounded bg-slate-200" />
            <div className="mt-4 space-y-2">
              <div className="h-9 rounded-lg bg-slate-200" />
              <div className="h-9 rounded-lg bg-slate-200" />
              <div className="h-9 rounded-lg bg-slate-200" />
            </div>
          </div>
        </aside>
      </div>
    </div>
  </article>
);

const SolutionPost: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [post, setPost] = useState<SolutionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotFound, setIsNotFound] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!slug) return;
    setIsLoading(true);
    setIsNotFound(false);
    setPost(null);
    const sid = new URLSearchParams(location.search).get('sid');
    const normalizedLang = i18n.language?.toLowerCase().startsWith('fr')
      ? 'fr'
      : i18n.language?.toLowerCase().startsWith('zh')
      ? 'zh'
      : 'en';

    const fields = `
  _id,
  "slug": slug.current,
  language,
  translationGroupId,
  title,
  mainImage,
  body,
  publishedAt,
  author,
  excerpt,
  headingPairs
`;

    const fetchByLanguagePreference = async (): Promise<SolutionDetail | null> => {
      const fetchBestByGroup = async (groupId: string): Promise<SolutionDetail | null> => {
        const exact = (await client.fetch(
          `*[_type == "solution" && translationGroupId == $groupId && language == $lang][0] {${fields}}`,
          { groupId, lang: normalizedLang }
        )) as SolutionDetail | null;
        if (exact) return exact;

        const english = (await client.fetch(
          `*[_type == "solution" && translationGroupId == $groupId && language == "en"][0] {${fields}}`,
          { groupId }
        )) as SolutionDetail | null;
        if (english) return english;

        return (await client.fetch(
          `*[_type == "solution" && translationGroupId == $groupId && !defined(language)][0] {${fields}}`,
          { groupId }
        )) as SolutionDetail | null;
      };

      const fetchBestBySlug = async (slugValue: string): Promise<SolutionDetail | null> => {
        const exact = (await client.fetch(
          `*[_type == "solution" && slug.current == $slug && language == $lang][0] {${fields}}`,
          { slug: slugValue, lang: normalizedLang }
        )) as SolutionDetail | null;
        if (exact) return exact;

        const english = (await client.fetch(
          `*[_type == "solution" && slug.current == $slug && language == "en"][0] {${fields}}`,
          { slug: slugValue }
        )) as SolutionDetail | null;
        if (english) return english;

        return (await client.fetch(
          `*[_type == "solution" && slug.current == $slug && !defined(language)][0] {${fields}}`,
          { slug: slugValue }
        )) as SolutionDetail | null;
      };

      if (sid) {
        const baseDoc = (await client.fetch(
          `*[_type == "solution" && _id == $sid][0] { _id, "slug": slug.current, translationGroupId }`,
          { sid }
        )) as SolutionLookupDoc | null;

        if (baseDoc?.translationGroupId) {
          const translated = await fetchBestByGroup(baseDoc.translationGroupId);
          if (translated) return translated;
        }

        if (baseDoc?.slug) {
          const bySlugFallback = await fetchBestBySlug(baseDoc.slug);
          if (bySlugFallback) return bySlugFallback;
        }
      }

      return await fetchBestBySlug(slug);
    };

    fetchByLanguagePreference()
      .then((data) => {
        if (!data) {
          setIsNotFound(true);
          setPost(null);
          return;
        }
        setPost(data);
      })
      .catch(() => {
        setIsNotFound(true);
        setPost(null);
      })
      .finally(() => setIsLoading(false));
  }, [slug, location.search, i18n.language]);

  useEffect(() => {
    if (!post) return;
    const rawHash = window.location.hash.replace('#', '').trim();
    if (!rawHash) return;

    const tryScroll = () => {
      const target =
        document.getElementById(rawHash) ||
        document.getElementById(rawHash.replace(/[–—]/g, '-'));
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
      const headingByAnchor = (post.headingPairs || []).find((item) => item.anchor === rawHash);
      const normalizedHash = normalizeAnchorText(rawHash);
      const fallbackHeading =
        headingByAnchor ||
        (post.headingPairs || []).find((item) => {
          const normalizedAnchor = normalizeAnchorText(item.anchor || '');
          const normalizedLabel = normalizeAnchorText(item.label || '');
          return (
            normalizedAnchor === normalizedHash ||
            normalizedAnchor.includes(normalizedHash) ||
            normalizedHash.includes(normalizedAnchor) ||
            isTokenMatch(normalizedHash, normalizedLabel)
          );
        });

      if (fallbackHeading?.anchor) {
        const fallbackTarget = document.getElementById(fallbackHeading.anchor);
        if (fallbackTarget) {
          fallbackTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.history.replaceState(null, '', `#${fallbackHeading.anchor}`);
          return true;
        }
      }

      if (!fallbackHeading || !contentRef.current) return false;
      const candidates = Array.from(contentRef.current.querySelectorAll('h1, h2, h3, p')) as HTMLElement[];
      const matchedNode = candidates.find((node) => {
        const nodeText = node.textContent || '';
        return (
          isTokenMatch(fallbackHeading.label, nodeText) ||
          normalizeSearchText(nodeText).includes(normalizeSearchText(fallbackHeading.label))
        );
      });
      if (matchedNode) {
        matchedNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
      return false;
    };

    if (tryScroll()) return;
    const timer = window.setTimeout(tryScroll, 120);
    return () => window.clearTimeout(timer);
  }, [post]);

  if (isLoading) {
    return <SolutionPostSkeleton />;
  }

  if (isNotFound || !post) {
    return (
      <div className="bg-slate-50 px-6 pb-16 pt-28 sm:pb-20">
        <div className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm sm:p-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700">{t('solutionPostSection.breadcrumbSolutions')}</p>
          <h1 className="mt-3 text-2xl font-bold text-slate-900 sm:text-3xl">{t('solutionPostSection.notFound.title')}</h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-slate-600 sm:text-base">
            {t('solutionPostSection.notFound.description')}
          </p>
          <div className="mt-8 flex items-center justify-center">
            <Link
              to="/solutions"
              className="inline-flex items-center rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700 transition hover:bg-teal-100"
            >
              {t('solutionPostSection.notFound.browseButton')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const hasManualHeadingPairs = !!(post.headingPairs && post.headingPairs.length > 0);
  const tocItems =
    post.headingPairs && post.headingPairs.length > 0 ? post.headingPairs : extractHeadingsFromBody(post.body);

const resolveAnchorForHeading = (rawLabel: string): string => {
    const normalizedLabel = normalizeSearchText(normalizeHeadingLabel(rawLabel));
    if (hasManualHeadingPairs) {
      const matchedItem = tocItems.find(
        (item) => {
          const itemLabel = normalizeSearchText(normalizeHeadingLabel(item.label));
          return (
            itemLabel === normalizedLabel ||
            itemLabel.includes(normalizedLabel) ||
            normalizedLabel.includes(itemLabel)
          );
        }
      );
      if (matchedItem?.anchor) return matchedItem.anchor;
    }
    const fallbackAnchor = slugify(normalizeHeadingLabel(rawLabel));
    return fallbackAnchor || `section-${toI18nKey(rawLabel) || 'item'}`;
  };

  const handleTocClick = (anchor: string, label: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const normalizedAnchor = String(anchor || '').trim();
    const targetById =
      document.getElementById(normalizedAnchor) ||
      document.getElementById(normalizedAnchor.replace(/[–—]/g, '-'));

    if (targetById) {
      targetById.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', `#${normalizedAnchor}`);
      return;
    }

    const container = contentRef.current;
    if (!container) return;

    const needle = normalizeSearchText(normalizeHeadingLabel(label));
    const candidates = Array.from(container.querySelectorAll('h2, h3, p')) as HTMLElement[];
    const match = candidates.find((node) => {
      const text = normalizeSearchText(normalizeHeadingLabel(node.textContent || ''));
      return (
        text === needle ||
        text.includes(needle) ||
        needle.includes(text) ||
        isTokenMatch(label, node.textContent || '')
      );
    });

    if (match) {
      match.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', `#${normalizedAnchor}`);
    }
  };

  const ptComponents = {
    types: {
      image: ({ value }: any) => {
        if (!value?.asset?._ref) return null;
        return (
          <figure className="my-8 overflow-hidden rounded-xl border border-slate-200">
            <img
              src={urlFor(value).width(768).fit('max').auto('format').quality(70).url()}
              srcSet={buildImageSrcSet(value, BODY_IMAGE_WIDTHS)}
              sizes="(max-width: 768px) 100vw, 80vw"
              alt={value.alt || 'Solution content visual'}
              className="h-auto w-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </figure>
        );
      },
    },
    block: {
      h1: ({ children }: any) => {
        const text = Array.isArray(children) ? children.join('') : String(children || '');
        const id = resolveAnchorForHeading(text);
        return (
          <h1 id={id} className="mt-10 scroll-mt-28 text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
            {children}
          </h1>
        );
      },
      h2: ({ children }: any) => {
        const text = Array.isArray(children) ? children.join('') : String(children || '');
        const id = resolveAnchorForHeading(text);
        return (
          <h2 id={id} className="mt-10 scroll-mt-28 text-2xl font-bold text-slate-900">
            {children}
          </h2>
        );
      },
      h3: ({ children }: any) => {
        const text = Array.isArray(children) ? children.join('') : String(children || '');
        const id = resolveAnchorForHeading(text);
        return (
          <h3 id={id} className="mt-8 scroll-mt-28 text-xl font-semibold text-slate-900">
            {children}
          </h3>
        );
      },
      normal: ({ children, value }: any) => {
        const text = getBlockText(value);
        if (isNumberedSectionLabel(text)) {
          const id = resolveAnchorForHeading(text);
          return (
            <h3 id={id} className="mt-8 scroll-mt-28 text-xl font-semibold text-slate-900">
              {children}
            </h3>
          );
        }
        return <p className="mt-5 text-base leading-8 text-slate-700">{children}</p>;
      },
      blockquote: ({ children }: any) => (
        <blockquote className="my-8 border-l-4 border-teal-500 bg-teal-50/40 px-5 py-3 text-slate-700">
          {children}
        </blockquote>
      ),
    },
    list: {
      bullet: ({ children }: any) => <ul className="mt-4 list-disc space-y-2 pl-6 text-slate-700">{children}</ul>,
      number: ({ children }: any) => <ol className="mt-4 list-decimal space-y-2 pl-6 text-slate-700">{children}</ol>,
    },
    marks: {
      link: ({ children, value }: any) => (
        <a
          href={value?.href}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-teal-700 underline decoration-teal-400 underline-offset-4"
        >
          {children}
        </a>
      ),
    },
  };

  return (
    <article className="bg-slate-50 pb-16 sm:pb-20">
      <SEO
        title={post.title}
        description={post.excerpt || post.title}
        ogType="article"
        ogImage={post.mainImage ? urlFor(post.mainImage).width(1200).height(630).url() : undefined}
        article={{ publishedTime: post.publishedAt, author: post.author || t('solutionPostSection.fallbackAuthor') }}
      />
      <ArticleSchema
        title={post.title}
        description={post.excerpt || post.title}
        url={`/solutions/${slug}`}
        image={post.mainImage ? urlFor(post.mainImage).url() : undefined}
        publishedAt={post.publishedAt}
        author={post.author || t('solutionPostSection.fallbackAuthor')}
      />
      <BreadcrumbSchema items={[{ name: t('solutionPostSection.breadcrumbSolutions'), url: '/solutions' }, { name: post.title, url: `/solutions/${slug}` }]} />

      <PageHero
        eyebrow={t('solutionPostSection.heroEyebrow')}
        title={post.title}
        description={post.excerpt}
        typedTitle
        action={{ label: t('solutionPostSection.backToSolutions'), to: '/solutions' }}
        titleClassName="mx-auto max-w-5xl text-balance lg:leading-[1.1]"
      />

      <div className="mx-auto max-w-[1240px] px-4 pt-6 sm:px-6 sm:pt-8 lg:px-8 lg:pt-10">
        {post.mainImage && (
          <div className="aspect-[1800/920] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm">
            <img
              src={urlFor(post.mainImage).width(960).height(491).fit('crop').auto('format').quality(72).url()}
              srcSet={buildImageSrcSet(post.mainImage, HERO_IMAGE_WIDTHS, 1800 / 920)}
              sizes="(max-width: 640px) 100vw, (max-width: 1280px) 90vw, 1240px"
              alt={post.title}
              className="h-full w-full object-cover"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-12">
          <div>
            <div
              ref={contentRef}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8 lg:p-10"
            >
              <div className="prose prose-slate max-w-none prose-headings:tracking-tight prose-headings:text-slate-900 prose-h1:mt-10 prose-h1:text-3xl prose-h1:font-extrabold prose-h1:leading-tight sm:prose-h1:text-4xl prose-h2:mt-10 prose-h2:text-2xl prose-h2:font-bold prose-h3:mt-8 prose-h3:text-xl prose-h3:font-semibold prose-p:mt-5 prose-p:text-base prose-p:leading-8 prose-p:text-slate-700 prose-strong:text-slate-900 prose-li:my-1 prose-li:leading-7 prose-ul:my-4 prose-ol:my-4">
              <PortableText value={post.body} components={ptComponents} />
              </div>
            </div>
          </div>

          {tocItems.length > 0 && (
            <aside className="lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-600">{t('solutionPostSection.onThisPage')}</h3>
                <div className="mt-4 flex max-h-[70vh] flex-col gap-2 overflow-auto pr-1">
                  {tocItems.map((item) => (
                    <a
                      key={item.anchor}
                      href={`#${item.anchor}`}
                      onClick={handleTocClick(item.anchor, item.label)}
                      className="inline-flex items-start rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium leading-5 text-slate-700 transition hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"
                    >
                      {t(`solutionsPageSection.tocLabels.${toI18nKey(item.label)}`, { defaultValue: item.label })}
                    </a>
                  ))}
                </div>
              </div>
            </aside>
          )}
        </div>
      </div>
    </article>
  );
};

export default SolutionPost;
